import axios from "axios";
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import { storageService } from "@/services/storageService";
import { errorHandler } from "@/services/errorHandler";

// 响应数据接口
export interface ResponseData<T = any> {
  code: number;
  message: string;
  data: T;
  success?: boolean;
  error?: string;
}

// 扩展 AxiosRequestConfig
export interface RequestConfig extends AxiosRequestConfig {
  // 是否显示错误提示
  showError?: boolean;
  // 是否显示加载提示
  showLoading?: boolean;
}

// 创建 axios 实例
const service: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "https://api.oxvalue.ai/api", // API 基础地址
  timeout: 10000, // 请求超时时间
  headers: {
    "Content-Type": "application/json;charset=UTF-8",
  },
});

// 请求拦截器
service.interceptors.request.use(
  (config: any) => {
    // Skip adding token for endpoints that don't need authentication
    const publicEndpoints = [
      "/auth/v2/user/auth_code/", // Send verification code
      "/auth/v2/user/token/", // Login (get tokens) - but not refresh
    ];

    const isPublicEndpoint = publicEndpoints.some((endpoint) => config.url?.includes(endpoint));
    const isRefreshEndpoint = config.url?.includes("/auth/v2/user/token/refresh/");

    // Don't add Authorization header for public endpoints or refresh endpoint
    if (!isPublicEndpoint && !isRefreshEndpoint) {
      const token = storageService.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error: AxiosError) => {
    console.error("请求错误:", error);
    return Promise.reject(error);
  }
);

// Token refresh flag to prevent infinite loops
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// 响应拦截器
service.interceptors.response.use(
  async (response: AxiosResponse<ResponseData>) => {
    const res = response.data;
    const originalRequest: any = response.config;

    // Handle 401 code in response body (token expired)
    if (res.code === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return service(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = storageService.getRefreshToken();
      const sid = storageService.getSid();

      if (!refreshToken || !sid) {
        storageService.clearAuthData();
        isRefreshing = false;
        return Promise.reject(new Error(res.error || "未授权"));
      }

      try {
        const { refreshAccessToken } = await import("./auth");
        const tokenResponse = await refreshAccessToken(refreshToken, sid);
        const tokenData = tokenResponse as any;

        storageService.saveTokens({
          access_token: tokenData.access_token,
          refresh_token: tokenData.refresh_token,
          sid: tokenData.sid,
        });

        originalRequest.headers.Authorization = `Bearer ${tokenData.access_token}`;
        processQueue(null, tokenData.access_token);
        isRefreshing = false;

        return service(originalRequest).then((retryResponse) =>
          retryResponse.data !== undefined ? retryResponse.data : retryResponse
        );
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        storageService.clearAuthData();
        return Promise.reject(refreshError);
      }
    }

    // 如果后端返回的 code 不是 200，则视为错误
    if (res.code !== undefined && res.code !== 200) {
      // Don't clear auth for 401 here - it's handled above
      if (errorHandler.shouldClearAuth(res.code) && res.code !== 401) {
        storageService.clearAuthData();
      }

      return Promise.reject(new Error(res.error || res.message || "请求失败"));
    }

    // 返回实际数据
    return res.data !== undefined ? res.data : res;
  },
  async (error: AxiosError) => {
    const originalRequest: any = error.config;

    // Handle 401 errors - token expired
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return service(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = storageService.getRefreshToken();
      const sid = storageService.getSid();

      if (!refreshToken || !sid) {
        storageService.clearAuthData();
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        const { refreshAccessToken } = await import("./auth");
        const response = await refreshAccessToken(refreshToken, sid);
        const tokenData = response as any;

        storageService.saveTokens({
          access_token: tokenData.access_token,
          refresh_token: tokenData.refresh_token,
          sid: tokenData.sid,
        });

        originalRequest.headers.Authorization = `Bearer ${tokenData.access_token}`;
        processQueue(null, tokenData.access_token);
        isRefreshing = false;

        return service(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        storageService.clearAuthData();
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors

    let message = "请求失败";
    if (error.response) {
      const status = error.response.status;
      message = errorHandler.getErrorMessage(status);

      if (errorHandler.shouldClearAuth(status)) {
        storageService.clearAuthData();
      }
    } else if (error.request) {
      message = "网络错误，请检查网络连接";
    } else {
      message = error.message || "请求失败";
    }

    return Promise.reject(new Error(message));
  }
);

// 封装请求方法
class Request {
  /**
   * GET 请求
   * @param url 请求地址
   * @param params 查询参数（会作为 URL 查询字符串）
   * @param config 请求配置
   */
  get<T = any>(url: string, params?: any, config?: RequestConfig): Promise<T> {
    return service.get<ResponseData<T>, T>(url, {
      ...config,
      params: params, // GET 请求的参数通过 params 传递
    });
  }

  /**
   * POST 请求
   */
  post<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return service.post<ResponseData<T>, T>(url, data, config);
  }

  /**
   * PUT 请求
   */
  put<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return service.put<ResponseData<T>, T>(url, data, config);
  }

  /**
   * DELETE 请求
   */
  delete<T = any>(url: string, config?: RequestConfig): Promise<T> {
    return service.delete<ResponseData<T>, T>(url, config);
  }

  /**
   * PATCH 请求
   */
  patch<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return service.patch<ResponseData<T>, T>(url, data, config);
  }

  /**
   * 上传文件
   */
  upload<T = any>(url: string, file: File | FormData, config?: RequestConfig): Promise<T> {
    const formData = file instanceof FormData ? file : new FormData();
    if (file instanceof File) {
      formData.append("file", file);
    }

    return service.post<ResponseData<T>, T>(url, formData, {
      ...config,
      headers: {
        "Content-Type": "multipart/form-data",
        ...config?.headers,
      },
    });
  }

  /**
   * 下载文件
   */
  download(url: string, filename?: string, config?: RequestConfig): Promise<void> {
    return service
      .get(url, {
        ...config,
        responseType: "blob",
      })
      .then((response: any) => {
        const blob = new Blob([response]);
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = filename || "download";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      });
  }
}

// 导出请求实例
export default new Request();

// 导出 axios 实例（用于特殊需求）
export { service as axiosInstance };
